import ClearIcon from '@mui/icons-material/Clear';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MedicationIcon from '@mui/icons-material/Medication';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotesIcon from '@mui/icons-material/Notes';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import SellIcon from '@mui/icons-material/Sell';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Grid, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import useRoles from 'common/hooks/useRoles';
import { DisableInvoiceGroup } from 'common/utils/featureFlags';
import { map, startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AdminCenterOptionTileView from './AdminCenterOptionTileView';

const AdminCenter = () => {
  const hasAccess = useRoles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const getCurrentPath = (path) => {
    const parts = path
      .split('/')
      .filter(Boolean)
      .filter((part) => !UUID_PATTERN.test(part));
    const formattedParts = map(parts, (part) => startCase(part));
    return formattedParts?.join(' / ');
  };

  useEffect(() => {
    document.title = 'Admin Center - SmartOS';
  }, []);

  const sections = [
    {
      title: 'Drugs & Catalog',
      tiles: [
        {
          visible: hasAccess.viewDrug,
          pathName: '/adminCenter/drugManagement',
          title: 'Drug Management',
          keywords: ['medication', 'medicine', 'ndc', 'product'],
          description: 'Add and manage drug profiles',
          icon: MedicationIcon,
        },
        {
          visible: hasAccess.viewCatalogManagement,
          pathName: '/adminCenter/catalogManagement',
          title: 'Catalog Management',
          keywords: ['drug catalog', 'formulary', 'products'],
          description: 'Add and manage the master drug catalog',
          icon: MenuBookIcon,
        },
        {
          visible: hasAccess.viewSigManagement,
          pathName: '/adminCenter/sigManagement',
          title: 'Sig Management',
          keywords: ['directions', 'instructions', 'dosing'],
          description: 'Manage sigs, their directions and catalog associations',
          icon: NotesIcon,
        },
        {
          visible: hasAccess.bulkFillHistoryUpdate,
          pathName: '/adminCenter/bulkNdcUpdate',
          title: 'Bulk Update NDC',
          keywords: ['fill history', 'ndc', 'mass update'],
          description: 'Update NDC in bulk for fill history',
          icon: PublishedWithChangesIcon,
        },
      ],
    },
    {
      title: 'Partners & Pharmacies',
      tiles: [
        {
          visible: hasAccess.viewPartnerAndPracticeManagement,
          pathName: '/adminCenter/PartnerPracticeManagement',
          title: 'Partner-Practice Management',
          keywords: ['partner', 'practice', 'clinic', 'customer', 'account'],
          description: 'Add and manage practice and Partner profiles',
          icon: HandshakeIcon,
        },
        // POM-5974 — the same screen the Inventory tab renders. Partner NDC
        // preference is both a partner setting and an allocation setting, so
        // it is reachable from either place rather than duplicated.
        {
          visible: hasAccess.viewPartnerNdcPreference,
          pathName: '/adminCenter/partnerPreferences',
          title: 'Partner NDC Preference',
          keywords: ['allocation', 'inventory', 'preferred ndc'],
          description: 'Set the preferred NDC per partner and drug',
          icon: TuneIcon,
        },
        {
          visible: hasAccess.viewExternalTransferPharmacy,
          pathName: '/adminCenter/externalPharmacy',
          title: 'External Pharmacy',
          keywords: ['transfer', 'pharmacies'],
          description: 'Add and manage external pharmacies',
          icon: LocalPharmacyIcon,
        },
        {
          visible: hasAccess.viewFacilityShippingDefault,
          pathName: '/adminCenter/facilitySettings',
          title: 'Shipping Service Default',
          keywords: ['shipping', 'carrier', 'facility', 'fedex', 'ups', 'usps'],
          description: 'Configure default shipping service per facility',
          icon: LocalShippingIcon,
        },
      ],
    },
    {
      title: 'Billing',
      tiles: [
        {
          visible: !DisableInvoiceGroup && hasAccess.viewInvoiceGroups,
          pathName: '/adminCenter/invoiceGroups',
          title: 'Invoicing',
          keywords: ['invoice', 'invoice group', 'billing', 'contact'],
          description: 'Manage Invoices',
          icon: ReceiptLongIcon,
        },
        {
          visible: hasAccess.viewProductPricing,
          pathName: '/adminCenter/productPricing',
          title: 'Product Pricing',
          keywords: ['price', 'pricing', 'override', 'invoice group', 'cost'],
          description: 'Manage default pricing and invoice group overrides',
          icon: SellIcon,
        },
      ],
    },
    {
      title: 'Systems',
      tiles: [
        {
          visible: hasAccess.viewSystem,
          pathName: '/adminCenter/requestStatus',
          title: 'Request Status',
          keywords: ['api', 'requests', 'logs', 'system'],
          description: 'View and manage requests from API',
          icon: SyncAltIcon,
        },
      ],
    },
  ];

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (tile) =>
    !normalizedQuery ||
    [tile.title, tile.description, ...tile.keywords].some((text) =>
      text.toLowerCase().includes(normalizedQuery)
    );

  const visibleSections = sections
    .map((section) => ({
      ...section,
      tiles: section.tiles.filter((tile) => tile.visible && matchesQuery(tile)),
    }))
    .filter((section) => section.tiles.length > 0);
  const matchingTiles = visibleSections.flatMap((section) => section.tiles);

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && normalizedQuery && matchingTiles.length > 0) {
      navigate(matchingTiles[0].pathName);
    } else if (event.key === 'Escape') {
      setQuery('');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Grid container sx={{ p: 3, flexShrink: 0 }} direction='column'>
        <Grid item container justifyContent='space-between' sx={{ mb: 1 }}>
          <Typography component='h1' variant='h5'>
            {getCurrentPath(pathname)}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {pathname === '/adminCenter' ? (
          <Stack sx={{ px: 3, pb: 3 }} spacing={4}>
            <TextField
              autoFocus
              size='small'
              placeholder='Search admin tools — e.g. pricing, NDC, shipping'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                inputProps: { 'aria-label': 'Search admin tools' },
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: query && (
                  <InputAdornment position='end'>
                    <IconButton size='small' aria-label='Clear search' onClick={() => setQuery('')}>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 480, bgcolor: 'background.paper' }}
            />

            {normalizedQuery && matchingTiles.length === 0 && (
              <Typography color='text.secondary'>
                No admin tools match &ldquo;{query.trim()}&rdquo;
              </Typography>
            )}

            {visibleSections.map((section) => (
              <Box component='section' key={section.title}>
                <Typography
                  component='h2'
                  variant='overline'
                  color='text.secondary'
                  sx={{ display: 'block', fontWeight: 600, mb: 1.5 }}
                >
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 2,
                  }}
                >
                  {section.tiles.map((tile) => (
                    <AdminCenterOptionTileView
                      key={tile.pathName}
                      pathName={tile.pathName}
                      title={tile.title}
                      description={tile.description}
                      icon={tile.icon}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
};

export default AdminCenter;
